import styled from 'styled-components';
import { cloneDeep } from 'lodash';
import {
  AccountClient,
  GET_TECHNICIANS_BY_CRITERIA,
  GET_REQUESTERS_BY_CRITERIA
} from '@nile/graphql';
import {
  ALL_MODULES,
  getTranslatedText,
  COMMON_CONSTANTS
} from '@nile/components';

const getRuleAttribute = (f) => {
  switch (f.name) {
    default:
      return f.name;
  }
};

/**
 * Default value to be populated in the filter component
 * @param {object} f
 * @param {object} condition
 */
const getDefaultValue = (f, condition, getConditionAttributes) => {
  const operands = cloneDeep(condition.operands);
  const ruleIdx = operands.findIndex((el) => {
    /*
     * List of changes done on FE side to handle kbnumber
     * 1. filter.js file
     * 2. InlineFilter.js (made changes on two place)
     * 3. Patch management view list page
     * 4. Asset patches
     */
    if (f.name === 'kbNumbers' || f.name === 'patchDetail.kbNumbers') {
      return el.attribute === `kbNumbers.kbNumber`;
    }
    if (f.name === 'patchDetail.kbNumbers.kbNumber') {
      return el.attribute === `patchDetail.kbNumbers.kbNumber`;
    }
    if (getConditionAttributes && getConditionAttributes(f)) {
      return getConditionAttributes(f);
    }
    if (f.dataType === 'entity') {
      return el.attribute === `${f.name}.name`;
    }
    return el.attribute === getRuleAttribute(f);
  });
  if (ruleIdx > -1) {
    const rule = operands[ruleIdx];
    switch (f.dataType) {
      case 'date':
      case 'datetime':
      case 'daterange':
        return rule.value;
      default:
        break;
    }
    switch (f.filterType) {
      case 'text':
      case 'numeric':
      case 'decimal':
      case 'paragraph':
      case 'read_only':
        return rule.value;
      default:
        return rule.value.map((el) => {
          return {
            name: el,
            value: el,
            label: el
          };
        });
    }
  }
  return '';
};

function getQuery(property) {
  switch (property.name) {
    case 'technician':
    case 'accountManager':
    case 'accountManager.name':
    case 'accountManager.email':
      return GET_TECHNICIANS_BY_CRITERIA;
    default:
      return GET_REQUESTERS_BY_CRITERIA;
  }
}

function getNameKey(property) {
  switch (property.name) {
    case 'technician':
    case 'accountManager':
    case 'accountManager.name':
    case 'accountManager.email':
      return 'getTechnicianList';
    default:
      return 'getRequesterList';
  }
}

function getAttribute(property) {
  switch (property?.name) {
    case 'secondaryContact.email':
    case 'primaryContact.email':
    case 'accountManager.email':
      return 'email';
    default:
      return 'name';
  }
}

/**
 * Used to fetch the values of dynamic queries
 * @param {object} client
 * @param {object} query
 * @param {object} params
 */
const fetchValues = async (client, query, params) => {
  const attribute = getAttribute(params.property);
  const nameKey = getNameKey(params.property);
  const variables = {
    listInfo: {
      condition: {
        joinOperator: 'AND',
        operands: [
          {
            attribute: attribute || 'name',
            operator: 'startsWith',
            value: params.inputValue
          }
        ]
      }
    }
  };
  const res = await client.query({
    query: query,
    variables: COMMON_CONSTANTS.IS_INSIDE_INPUT_VARIABLES[nameKey]
      ? { input: variables }
      : variables
  });
  const allOptions = res?.data?.[nameKey]
    ? res.data[nameKey].userList.map((el) => {
        return {
          name: `${el[attribute]}`,
          value: `${el[attribute]}`,
          label: `${el[attribute]}`
        };
      })
    : [];
  return allOptions;
};

const getAsyncOptions = (property) => {
  return { client: AccountClient, query: getQuery(property) };
};

/**
 * This method updates the particular condition in a set of rules with multiple operands.
 * Also calls the parent with the updated conditions.
 * @param {object} params
 */
const updateConditions = (params) => {
  const {
    condition,
    selectedField,
    viewConditions,
    setConditions,
    getCustomAttributes
  } = params;
  if (condition?.operands?.length) {
    const currentConditions = cloneDeep(viewConditions) || {
      joinOperator: 'AND',
      operands: []
    };
    let updatedCondition = {};
    const ruleIdx = currentConditions.operands.findIndex((el) => {
      if (getCustomAttributes && getCustomAttributes(selectedField)) {
        return el?.attribute === getCustomAttributes(selectedField);
      }
      return selectedField.dataType === 'entity'
        ? el.attribute === `${selectedField.name}.name`
        : el.attribute === getRuleAttribute(selectedField);
    });

    if (ruleIdx > -1) {
      currentConditions.operands[ruleIdx] = condition.operands[0];
      updatedCondition = currentConditions;
    } else {
      updatedCondition = {
        ...currentConditions,
        operands: currentConditions.operands.concat(condition.operands)
      };
    }
    setConditions(updatedCondition);
  } else {
    const currentConditions = cloneDeep(viewConditions);
    if (currentConditions) {
      const ruleIdx = currentConditions.operands.findIndex((el) => {
        if (getCustomAttributes && getCustomAttributes(selectedField)) {
          return el?.attribute === getCustomAttributes(selectedField);
        }
        return selectedField.dataType === 'entity'
          ? el.attribute === `${selectedField.name}.name`
          : el.attribute === getRuleAttribute(selectedField);
      });
      if (ruleIdx > -1) {
        currentConditions.operands.splice(ruleIdx, 1);
        setConditions(
          currentConditions?.operands?.length
            ? {
                ...currentConditions,
                operands: currentConditions.operands
              }
            : null
        );
      }
    }
  }
};

/**
 * Default date operators for date filters
 * @param {object} intl
 */

const getDateOperators = (intl) => {
  const dateOperators = [
    {
      key: 'placeholder.today',
      label: getTranslatedText(intl, 'placeholder.today'),
      value: " placeholder.today',"
    },
    {
      key: 'placeholder.tomorrow',
      label: getTranslatedText(intl, 'placeholder.tomorrow'),
      value: " placeholder.tomorrow',"
    },
    {
      key: 'placeholder.yesterday',
      label: getTranslatedText(intl, 'placeholder.yesterday'),
      value: " placeholder.yesterday',"
    },
    {
      key: 'placeholder.last.week',
      label: getTranslatedText(intl, 'placeholder.last.week'),
      value: 'placeholder.last.week'
    },
    {
      key: 'placeholder.this.week',
      label: getTranslatedText(intl, 'placeholder.this.week'),
      value: 'placeholder.this.week'
    },
    {
      key: 'placeholder.next.week',
      label: getTranslatedText(intl, 'placeholder.next.week'),
      value: 'placeholder.next.week'
    },
    {
      key: 'placeholder.last.month',
      label: getTranslatedText(intl, 'placeholder.last.month'),
      value: 'placeholder.last.month'
    },
    {
      key: 'placeholder.this.month',
      label: getTranslatedText(intl, 'placeholder.this.month'),
      value: 'placeholder.this.month'
    },
    {
      key: 'placeholder.next.month',
      label: getTranslatedText(intl, 'placeholder.next.month'),
      value: 'placeholder.next.month'
    },
    {
      key: 'placeholder.last.quarter',
      label: getTranslatedText(intl, 'placeholder.last.quarter'),
      value: 'placeholder.last.quarter'
    },
    {
      key: 'placeholder.this.quarter',
      label: getTranslatedText(intl, 'placeholder.this.quarter'),
      value: 'placeholder.this.quarter'
    },
    {
      key: 'placeholder.next.quarter',
      label: getTranslatedText(intl, 'placeholder.next.quarter'),
      value: 'placeholder.next.quarter'
    },
    {
      key: 'placeholder.last.30.days',
      label: getTranslatedText(intl, 'placeholder.last.30.days'),
      value: 'placeholder.last.30.days'
    },
    {
      key: 'placeholder.last.six.months',
      label: getTranslatedText(intl, 'placeholder.last.six.months'),
      value: 'placeholder.last.six.months'
    }
  ];
  return dateOperators;
};

const getUnifiedConditions = (params) => {
  const { conditions, defaultCondition, updatedCondition } = params;
  let complexCondition = cloneDeep(conditions);
  const advancedCondition = complexCondition?.operands?.[1] || [];
  if (updatedCondition) {
    complexCondition = {
      joinOperator: 'AND',
      operands: [updatedCondition].concat(advancedCondition)
    };
  } else if (complexCondition) {
    complexCondition = {
      joinOperator: 'AND',
      operands: [defaultCondition].concat(advancedCondition)
    };
  } else {
    complexCondition = null;
  }
  return complexCondition;
};

export {
  getDefaultValue,
  updateConditions,
  getAsyncOptions,
  fetchValues,
  getDateOperators,
  getUnifiedConditions
};
